import * as React from 'react';

import { isEmpty } from 'lodash';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Notification } from '@coursera/coursera-ui';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

import { IMPORT_ACCREDITATION_TAGS_TEMPLATE_URL } from 'bundles/enterprise-admin-constants/ProgramMembersConstants';
import CSVPreUploadPreview from 'bundles/enterprise-admin-users/components/csvImportWidget/CSVPreUploadPreviewV2';
import FileUploadWidget from 'bundles/enterprise-admin-users/components/csvImportWidget/FileUploadWidgetV2';
import { uploadAccreditationTags } from 'bundles/internal-enterprise/components/utils/CurriculumBuilderApiUtils';
import Button from 'bundles/internal-ui/components/buttons/Button';
import Card from 'bundles/internal-ui/components/cards/Card';
import TextBox from 'bundles/internal-ui/components/forms/TextBox';

import _t from 'i18n!nls/internal-enterprise';

import 'css!./__styles__/EnterpriseToolsHome';

type State = {
  orgId: string;
  file: File | null;
  errorMessage: string | null;
  successMessage: string | null;
  loading: boolean;
};

const notificationStyle = {
  boxSizing: 'border-box',
  fontSize: '14px',
  marginTop: 20,
};

export default class CurriculumBuilder extends React.Component<{}, State> {
  state: State = {
    orgId: '',
    file: null,
    errorMessage: null,
    successMessage: null,
    loading: false,
  };

  handleUpdateText = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ orgId: event.target.value });
  };

  onFileDrop = ({ files }: { files: File[] }) => {
    if (!isEmpty(files)) {
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      this.setState({ file: files[0] });
    }
  };

  getFileWidgetText = () => {
    const { file } = this.state;
    return file ? _t('Will replace current file') : _t('You can upload 1 file at a time');
  };

  handleDeletingFile = () => {
    this.setState({ file: null });
  };

  onSuccessfulUpload = () => {
    const { orgId } = this.state;

    this.setState({
      loading: false,
      errorMessage: null,
      successMessage: _t('Successfully imported new accreditation tags to organizationId: #{orgId}', { orgId }),
      file: null,
    });
  };

  onFailedUpload = () => {
    this.setState({
      loading: false,
      errorMessage: _t('Uploading accreditation tags failed'),
      successMessage: null,
    });
  };

  handleUpload = async () => {
    const { file, orgId } = this.state;

    if (!orgId) {
      this.setState({ errorMessage: _t('Please provide an organization id to continue'), successMessage: null });
    } else if (!file) {
      this.setState({ errorMessage: _t('Please upload a file to continue'), successMessage: null });
    } else {
      this.setState({ loading: true, successMessage: null, errorMessage: null });
      await uploadAccreditationTags(file, orgId, this.onSuccessfulUpload, this.onFailedUpload);
    }
  };

  render() {
    const { orgId, errorMessage, successMessage, file, loading } = this.state;
    return (
      <div className="rc-EnterpriseToolsHome" data-testid="curriculum-builder-error-notification">
        <Card>
          <h3>{_t('Add accreditation tags to an organization')}</h3>
          <FormattedHTMLMessage
            message={_t(
              `
              <p class="download-csv-link"><span><a class="download-csv-link" href={csvTemplateLink} target="_blank">Download the CSV Template </a></span>
              to add accreditation tags. </p>
            `
            )}
            csvTemplateLink={IMPORT_ACCREDITATION_TAGS_TEMPLATE_URL}
          />
          <TextBox
            placeholder={_t('Organization Id')}
            onUpdateField={this.handleUpdateText}
            currentValue={orgId}
            name="orgId"
          />
          <div className="vertical-box align-items-absolute-center p-t-2 p-b-3" style={{ minHeight: '100px' }}>
            {loading ? (
              <div>
                <SvgLoaderSignal size={64} />
                <p className="m-t-1">{_t('Uploading...')}</p>
              </div>
            ) : (
              <FileUploadWidget
                accept={{ 'text/csv': ['.csv'] }}
                singleFileOnly={true}
                defaultFileMessage={this.getFileWidgetText()}
                onDrop={this.onFileDrop}
              >
                {file && (
                  <div style={{ marginTop: 10 }}>
                    <CSVPreUploadPreview files={[file]} onDelete={this.handleDeletingFile} />
                  </div>
                )}
              </FileUploadWidget>
            )}
          </div>
          <Button
            text={_t('Upload Accreditation Tags')}
            btnStyle="info"
            onClick={this.handleUpload}
            data-e2e="curriculum-builder-button"
          />
          {errorMessage && (
            <div>
              <Notification
                style={notificationStyle}
                message={errorMessage}
                type="error"
                data-e2e="curriculum-builder-error-notification"
              />
            </div>
          )}
          {successMessage && (
            <div>
              <Notification
                message={successMessage}
                type="success"
                style={notificationStyle}
                data-e2e="curriculum-builder-success-notification"
              />
            </div>
          )}
        </Card>
      </div>
    );
  }
}
